import { createContext, useCallback, useContext } from 'react'

import { getUrlWithParams } from 'core-components/helpers/url'
import { useNavigate } from 'core/hooks/useNavigate'

export interface ICockpitRemotePartner {
  partnerId: string
  partnerName: string
  /** The partner's own plan page, on the cockpit's year and the offering the status was raised
   *  for, in the planning view the cockpit belongs to. */
  partnerUrl: string
}

/**
 * The partner a status section answers for when the cockpit is not that partner's own page. Empty
 * on the single-partner cockpit, where the section's partner is the page's.
 */
const CockpitRemotePartnerContext = createContext<ICockpitRemotePartner | null>(null)

export const CockpitRemotePartnerProvider = CockpitRemotePartnerContext.Provider

/** The partner a scoped part stands for, or `null` on the partner's own cockpit. */
export const useCockpitRemotePartner = () => useContext(CockpitRemotePartnerContext)

/**
 * How a cockpit part takes its reader to a place on the partner's page.
 *
 * On the partner's own page a section moves the page to the tab or section by its URL params. From
 * the manager cockpit there is no partner page under it, so the same params open the partner's plan
 * page in a new tab instead; `isRemote` tells a section its own dialogs are not reachable there.
 */
export const useCockpitSectionNavigation = () => {
  const navigate = useNavigate()
  const remote = useCockpitRemotePartner()

  /** Opens an address that already names the partner, e.g. a plan-review section. */
  const openUrl = useCallback(
    (url: string) => {
      if (remote) {
        window.open(url, '_blank', 'noreferrer')
        return
      }

      navigate(url)
    },
    [navigate, remote]
  )

  /** Opens a tab or section of the partner's page by the URL params that name it. */
  const openSection = useCallback(
    (params: Record<string, unknown> = {}) => openUrl(getUrlWithParams(params, remote?.partnerUrl)),
    [openUrl, remote]
  )

  return { isRemote: Boolean(remote), openSection, openUrl }
}
